import React, { useRef, useCallback, useState, useEffect } from "react";
import { FaVolumeUp, FaVolumeDown, FaVolumeMute, FaUserAlt } from "react-icons/fa";

const SOUND_PREFERENCES = {
    ALL: "all",
    PUBLIC: "public",
    CURRENT: "current",
    MUTED: "muted",
};

const STORAGE_KEY = "sound_preference";

/**
 * Custom hook for sound management with muting preferences
 * @param {string} soundPath - Path to the sound file
 * @param {string} soundType - Type of sound ('public' or 'current')
 * @param {number} volume - Volume level (0-1)
 */
const useSound = (soundPath, soundType = "public", volume = 1) => {
    const audioRef = useRef(null);
    const [soundPreference, setSoundPreference] = useState(() => {
        // Initialize from localStorage or default to ALL
        return localStorage.getItem(STORAGE_KEY) || SOUND_PREFERENCES.ALL;
    });

    // Update localStorage whenever soundPreference changes
    useEffect(() => {
        localStorage.setItem(STORAGE_KEY, soundPreference);
    }, [soundPreference]);

    const shouldPlaySound = useCallback(() => {
        const currentPreference = localStorage.getItem(STORAGE_KEY);
        switch (currentPreference) {
            case SOUND_PREFERENCES.ALL:
                return true;
            case SOUND_PREFERENCES.PUBLIC:
                return soundType === "public";
            case SOUND_PREFERENCES.CURRENT:
                return soundType === "current";
            case SOUND_PREFERENCES.MUTED:
                return false;
            default:
                return true;
        }
    }, [soundType]);

    const play = useCallback(() => {
        if (!shouldPlaySound()) return;

        if (!audioRef.current) {
            audioRef.current = new Audio(soundPath);
            audioRef.current.volume = Math.max(0, Math.min(1, volume));
        }

        const playPromise = audioRef.current.play();

        if (playPromise !== undefined) {
            playPromise
                .then((_) => {
                    // Automatic playback started!
                })
                .catch((error) => {
                    console.log("Playback prevented. This is normal on mobile devices.", error);
                });
        }
    }, [soundPath, volume, shouldPlaySound]);

    const handlePreferenceChange = (newPreference) => {
        setSoundPreference(newPreference);
        localStorage.setItem(STORAGE_KEY, newPreference);
    };

    const SoundControl = () => {
        const [isOpen, setIsOpen] = useState(false);
        const dropdownRef = useRef(null);
        const currentPreference = localStorage.getItem(STORAGE_KEY) || SOUND_PREFERENCES.ALL;

        // Close dropdown when clicking outside
        useEffect(() => {
            const handleClickOutside = (event) => {
                if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
                    setIsOpen(false);
                }
            };
            document.addEventListener("mousedown", handleClickOutside);
            return () => document.removeEventListener("mousedown", handleClickOutside);
        }, []);

        const getCurrentIcon = () => {
            switch (currentPreference) {
                case SOUND_PREFERENCES.ALL:
                    return <FaVolumeUp className="text-white" />;
                case SOUND_PREFERENCES.PUBLIC:
                    return <FaVolumeDown className="text-white" />;
                case SOUND_PREFERENCES.CURRENT:
                    return <FaUserAlt className="text-white" />;
                case SOUND_PREFERENCES.MUTED:
                    return <FaVolumeMute className="text-white" />;
                default:
                    return <FaVolumeUp className="text-white" />;
            }
        };

        return (
            <div className="sound-control relative text-white mx-[12px] mt-[4px]" ref={dropdownRef}>
                <button
                    className="sound-button "
                    onClick={() => setIsOpen(!isOpen)}
                    title="Sound Settings"
                >
                    {getCurrentIcon()}
                </button>

                {isOpen && (
                    <div className="sound-dropdown absolute right-0 mt-2 py-2 w-48 bg-[#1f2937] rounded-lg shadow-xl">
                        {Object.values(SOUND_PREFERENCES).map((preference) => (
                            <button
                                key={preference}
                                className="w-full px-4 py-2 text-left flex items-center gap-2 hover:bg-gray-700"
                                onClick={() => {
                                    handlePreferenceChange(preference);
                                    setIsOpen(false);
                                }}
                            >
                                {currentPreference === preference && (
                                    <span className="absolute left-2">✓</span>
                                )}
                                <span className="ml-4">
                                    {preference === SOUND_PREFERENCES.ALL && (
                                        <FaVolumeUp className="text-white inline mr-2" />
                                    )}
                                    {preference === SOUND_PREFERENCES.PUBLIC && (
                                        <FaVolumeDown className="text-white inline mr-2" />
                                    )}
                                    {preference === SOUND_PREFERENCES.CURRENT && (
                                        <FaUserAlt className="text-white inline mr-2" />
                                    )}
                                    {preference === SOUND_PREFERENCES.MUTED && (
                                        <FaVolumeMute className="text-white inline mr-2" />
                                    )}
                                    {preference === SOUND_PREFERENCES.ALL && "Play All Sounds"}
                                    {preference === SOUND_PREFERENCES.PUBLIC &&
                                        "Public Sounds Only"}
                                    {preference === SOUND_PREFERENCES.CURRENT &&
                                        "Current Sounds Only"}
                                    {preference === SOUND_PREFERENCES.MUTED && "Mute All Sounds"}
                                </span>
                            </button>
                        ))}
                    </div>
                )}
            </div>
        );
    };

    return { play, SoundControl, soundPreference };
};

export default useSound;
